// ============================================================================
// Sender-side email composer — the direct fix for Ben's fourth complaint,
// that the outbound email was generic and hard-coded with no way to add a
// note or any signing instructions.
//
// Design rule throughout: EVERY field has a working default, so a sender in a
// hurry never has to open this panel to send. Everything is overridable, and
// nothing is mandatory.
//
// The "Note to signer" is a first-class field, stored separately from the
// email body, because it renders in TWO places — the outbound email AND the
// instruction panel at the top of the signing page.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Check, ChevronDown, Loader2, Mail, Plus, Save,
} from 'lucide-react';
import {
  BODY_MAX, MERGE_VARIABLES, MERGE_VARIABLE_LABELS, MergeVariable, NOTE_MAX,
  SUBJECT_MAX, applyMerge, htmlToPreviewText, unknownVariables,
  unresolvedVariables,
} from './merge-vars';
import {
  DOCUMENT_TYPE_LABELS, EnvelopeCopy, EsignDocumentType, EsignTemplate,
  listTemplates, saveTemplate,
} from './envelope-lib';
import { errorMessage } from '../../shared/errors';

// AFB-0075 / PR 2 — the document-type vocabulary moved to envelope-lib when the
// onboarding types were added, so this panel and the template manager on
// /money/envelopes cannot drift into two different lists of the same CHECK.

/** What the built-in default produces, shown so the sender knows the baseline. */
function defaultSubject(documentTitle: string): string {
  return `Signature requested — ${documentTitle}`;
}

export interface EmailComposerProps {
  envelopeTitle: string;
  senderName: string;
  /** First recipient's name, used for the live preview only. */
  sampleRecipientName: string;
  expirationDays: number;
  value: EnvelopeCopy;
  disabled: boolean;
  saving: boolean;
  savedAt: number | null;
  onChange: (patch: Partial<EnvelopeCopy>) => void;
  onSave: () => void;
  onPreviewAsSigner: () => void;
  /**
   * Bumped when the send confirmation's "Edit the email first" is used, so
   * the sender lands with the panel already open rather than having to find
   * and expand it a second time.
   */
  openSignal?: number;
}

export function EmailComposer({
  envelopeTitle, senderName, sampleRecipientName, expirationDays, value,
  disabled, saving, savedAt, onChange, onSave, onPreviewAsSigner, openSignal,
}: EmailComposerProps) {
  const [open, setOpen] = useState(false);

  // "Edit the email first" from the send confirmation lands here.
  useEffect(() => {
    if (openSignal) setOpen(true);
  }, [openSignal]);
  const [templates, setTemplates] = useState<EsignTemplate[]>([]);
  const [tplError, setTplError] = useState<string | null>(null);
  const [showSaveTpl, setShowSaveTpl] = useState(false);
  const [tplName, setTplName] = useState('');
  const [tplType, setTplType] = useState<EsignDocumentType>('other');
  const [tplBusy, setTplBusy] = useState(false);
  const [activeField, setActiveField] = useState<'subject' | 'body' | null>(null);

  useEffect(() => {
    listTemplates().then(setTemplates).catch((e: Error) => setTplError(e.message));
  }, []);

  const vars: Partial<Record<MergeVariable, string>> = useMemo(() => ({
    recipient_name: sampleRecipientName,
    document_name: envelopeTitle,
    sender_name: senderName,
    company_name: 'Terminal Cleaning Services',
    expiration_days: String(expirationDays),
    signing_link: 'https://app.terminalcleaning.com/sign/…',
  }), [sampleRecipientName, envelopeTitle, senderName, expirationDays]);

  const effectiveSubject = value.email_subject?.trim()
    ? value.email_subject
    : defaultSubject(envelopeTitle);
  const usingDefaultSubject = !value.email_subject?.trim();
  const usingDefaultBody = !value.email_body?.trim();

  // Pre-send validation: a variable that will not resolve, or one that is not
  // a variable we recognise at all (i.e. a typo).
  const problems = useMemo(() => {
    const text = `${value.email_subject ?? ''}\n${value.email_body ?? ''}`;
    return {
      unresolved: unresolvedVariables(text, vars),
      unknown: unknownVariables(text),
    };
  }, [value.email_subject, value.email_body, vars]);

  const subjectLen = (value.email_subject ?? '').length;
  const subjectOver = subjectLen > SUBJECT_MAX;

  function insertVariable(v: MergeVariable) {
    const token = `{{${v}}}`;
    if (activeField === 'subject') {
      onChange({ email_subject: `${value.email_subject ?? ''}${token}` });
    } else {
      onChange({
        email_body: `${value.email_body ?? ''}${token}`,
      });
    }
  }

  function applyTemplate(t: EsignTemplate) {
    onChange({
      email_subject: t.email_subject,
      email_body: t.email_body,
      note_to_signer: t.note_to_signer,
    });
  }

  async function doSaveTemplate() {
    setTplBusy(true);
    setTplError(null);
    try {
      const t = await saveTemplate({
        name: tplName.trim(),
        document_type: tplType,
        email_subject: value.email_subject,
        email_body: value.email_body,
        note_to_signer: value.note_to_signer,
      });
      setTemplates((prev) => [...prev, t]);
      setShowSaveTpl(false);
      setTplName('');
    } catch (e) {
      setTplError(errorMessage(e, 'Could not save the template.'));
    } finally {
      setTplBusy(false);
    }
  }

  return (
    <section className="rounded-md border border-tcs-border bg-tcs-raised">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-3 text-left"
      >
        <Mail className="h-4 w-4 shrink-0 text-tcs-accent-ink" aria-hidden />
        <span className="flex-1 text-sm font-semibold text-tcs-text">
          Email &amp; note to signer
        </span>
        <span className="text-xs text-tcs-text-muted">
          {usingDefaultSubject && usingDefaultBody && !value.note_to_signer
            ? 'Using defaults'
            : 'Customised'}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-tcs-text-muted transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {!open && (
        <p className="border-t border-tcs-border px-3 py-2 text-xs text-tcs-text-muted">
          Ready to send as-is — open only if you want to change the wording or
          add a note.
        </p>
      )}

      {open && (
        <div className="border-t border-tcs-border p-3">
          {/* ── templates ─────────────────────────────────────────────── */}
          {templates.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-medium text-tcs-text-muted">
                Start from a template
              </p>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {templates.map((t) => (
                  <button
                    key={t.template_id}
                    type="button"
                    disabled={disabled}
                    onClick={() => applyTemplate(t)}
                    className="rounded-md border border-tcs-border px-2.5 py-1.5 text-xs font-medium text-tcs-text hover:bg-tcs-bg disabled:opacity-50"
                    title={DOCUMENT_TYPE_LABELS[t.document_type]}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── subject ───────────────────────────────────────────────── */}
          <label htmlFor="esign-subject" className="block text-xs font-medium text-tcs-text">
            Subject
          </label>
          <input
            id="esign-subject"
            value={value.email_subject ?? ''}
            disabled={disabled}
            onFocus={() => setActiveField('subject')}
            onChange={(e) => onChange({ email_subject: e.target.value })}
            placeholder={defaultSubject(envelopeTitle)}
            className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${
              subjectOver ? 'border-st-danger-border-strong' : 'border-tcs-border'
            }`}
          />
          <p className={`mt-0.5 text-right text-[11px] ${
            subjectOver ? 'font-medium text-st-danger-fg' : 'text-tcs-text-muted'
          }`}>
            {subjectLen}/{SUBJECT_MAX}
            {subjectOver && ' — long subjects get cut off in most inboxes'}
          </p>

          {/* ── body ──────────────────────────────────────────────────── */}
          <label htmlFor="esign-body" className="mt-3 block text-xs font-medium text-tcs-text">
            Message
          </label>
          <textarea
            id="esign-body"
            rows={6}
            value={value.email_body ?? ''}
            disabled={disabled}
            maxLength={BODY_MAX}
            onFocus={() => setActiveField('body')}
            onChange={(e) => onChange({ email_body: e.target.value })}
            placeholder={`Leave blank to use the standard message:\n\nHello {{recipient_name}},\n${senderName} at Terminal Cleaning Services has requested your signature on ${envelopeTitle}.`}
            className="mt-1 w-full rounded-md border border-tcs-border px-3 py-2 font-mono text-xs"
          />

          {/* ── merge variables ───────────────────────────────────────── */}
          <div className="mt-2">
            <p className="text-[11px] text-tcs-text-muted">
              Insert into the {activeField === 'subject' ? 'subject' : 'message'}:
            </p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {MERGE_VARIABLES.map((v) => (
                <button
                  key={v}
                  type="button"
                  disabled={disabled}
                  onClick={() => insertVariable(v)}
                  className="rounded border border-dashed border-tcs-border px-1.5 py-1 font-mono text-[11px] text-tcs-accent-ink hover:bg-tcs-bg disabled:opacity-50"
                  title={MERGE_VARIABLE_LABELS[v]}
                >
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          </div>

          {(problems.unknown.length > 0 || problems.unresolved.length > 0) && (
            <div className="mt-2 flex items-start gap-2 rounded-md border border-st-warn-border-strong bg-st-warn-bg p-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-st-warn-fg" aria-hidden />
              <div className="text-xs text-st-warn-fg-strong">
                {problems.unknown.length > 0 && (
                  <p>
                    <strong>Not a real variable:</strong>{' '}
                    {problems.unknown.map((u) => `{{${u}}}`).join(', ')} — this
                    will be sent to the recipient exactly as written.
                  </p>
                )}
                {problems.unresolved.length > 0 && (
                  <p className={problems.unknown.length > 0 ? 'mt-1' : undefined}>
                    <strong>Nothing to fill in:</strong>{' '}
                    {problems.unresolved.map((u) => `{{${u}}}`).join(', ')} — no
                    value is available, so it will be left as-is.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ── note to signer ────────────────────────────────────────── */}
          <label htmlFor="esign-note" className="mt-4 block text-xs font-medium text-tcs-text">
            Note to signer
          </label>
          <p className="text-[11px] text-tcs-text-muted">
            Shown in the email <em>and</em> at the top of the signing page. Use it
            for anything they should check before signing.
          </p>
          <textarea
            id="esign-note"
            rows={3}
            value={value.note_to_signer ?? ''}
            disabled={disabled}
            maxLength={NOTE_MAX}
            onChange={(e) => onChange({ note_to_signer: e.target.value })}
            placeholder="e.g. Page 3 has the updated rate — check it before you sign."
            className="mt-1 w-full rounded-md border border-tcs-border px-3 py-2 text-sm"
          />

          {/* ── reminders + expiry ────────────────────────────────────── */}
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="esign-remind" className="block text-xs font-medium text-tcs-text">
                Remind every
              </label>
              <select
                id="esign-remind"
                disabled={disabled}
                value={value.reminder_every_days ?? ''}
                onChange={(e) => onChange({
                  reminder_every_days: e.target.value ? Number(e.target.value) : null,
                })}
                className="mt-1 w-full rounded-md border border-tcs-border px-2 py-2 text-sm"
              >
                <option value="">No reminders</option>
                {[2, 3, 5, 7, 14].map((d) => (
                  <option key={d} value={d}>{d} days</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="esign-expiry" className="block text-xs font-medium text-tcs-text">
                Link expires in
              </label>
              <select
                id="esign-expiry"
                disabled={disabled}
                value={value.expires_in_days ?? ''}
                onChange={(e) => onChange({
                  expires_in_days: e.target.value ? Number(e.target.value) : null,
                })}
                className="mt-1 w-full rounded-md border border-tcs-border px-2 py-2 text-sm"
              >
                <option value="">{expirationDays} days (default)</option>
                {[7, 14, 30, 60, 90].map((d) => (
                  <option key={d} value={d}>{d} days</option>
                ))}
              </select>
            </div>
          </div>

          {/* ── live preview ──────────────────────────────────────────── */}
          <div className="mt-4 rounded-md border border-tcs-border bg-tcs-bg p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
              What {sampleRecipientName || 'the recipient'} will see
            </p>
            <p className="mt-1.5 text-sm font-semibold text-tcs-text">
              {applyMerge(effectiveSubject, vars)}
            </p>
            <p className="mt-1 whitespace-pre-wrap text-xs text-tcs-text-muted">
              {usingDefaultBody
                ? `Hello ${sampleRecipientName || 'there'},\n\n${senderName} at Terminal Cleaning Services has requested your signature on ${envelopeTitle}.`
                : htmlToPreviewText(applyMerge(value.email_body ?? '', vars))}
            </p>
            {value.note_to_signer?.trim() && (
              <div className="mt-2 border-l-4 border-tcs-accent-ink bg-tcs-raised px-2 py-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                  Note from {senderName}
                </p>
                <p className="whitespace-pre-wrap text-xs text-tcs-text">
                  {value.note_to_signer}
                </p>
              </div>
            )}
            <p className="mt-2 text-xs text-tcs-accent-ink underline">
              Review and sign the document
            </p>
          </div>

          {/* ── actions ───────────────────────────────────────────────── */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onSave}
              disabled={disabled || saving || subjectOver}
              className="inline-flex min-h-[40px] items-center gap-1.5 rounded-md bg-tcs-accent-ink px-3 text-sm font-semibold text-tcs-accent-on disabled:opacity-50"
            >
              {saving
                ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                : <Save className="h-4 w-4" aria-hidden />}
              Save
            </button>
            <button
              type="button"
              onClick={onPreviewAsSigner}
              className="min-h-[40px] rounded-md border border-tcs-border px-3 text-sm font-medium text-tcs-text"
            >
              Preview as signer
            </button>
            <button
              type="button"
              onClick={() => setShowSaveTpl((v) => !v)}
              disabled={disabled}
              className="inline-flex min-h-[40px] items-center gap-1 rounded-md border border-tcs-border px-3 text-sm font-medium text-tcs-text disabled:opacity-50"
            >
              <Plus className="h-4 w-4" aria-hidden />
              Save as template
            </button>
            {savedAt && !saving && (
              <span className="inline-flex items-center gap-1 text-xs text-st-ok-fg">
                <Check className="h-3.5 w-3.5" aria-hidden /> Saved
              </span>
            )}
          </div>

          {showSaveTpl && (
            <div className="mt-3 rounded-md border border-tcs-border bg-tcs-bg p-3">
              <label htmlFor="tpl-name" className="block text-xs font-medium text-tcs-text">
                Template name
              </label>
              <input
                id="tpl-name"
                value={tplName}
                onChange={(e) => setTplName(e.target.value)}
                placeholder="e.g. Dialysis centre — standard"
                className="mt-1 w-full rounded-md border border-tcs-border px-3 py-2 text-sm"
              />
              <label htmlFor="tpl-type" className="mt-2 block text-xs font-medium text-tcs-text">
                Document type
              </label>
              <select
                id="tpl-type"
                value={tplType}
                onChange={(e) => setTplType(e.target.value as EsignDocumentType)}
                className="mt-1 w-full rounded-md border border-tcs-border px-2 py-2 text-sm"
              >
                {(Object.keys(DOCUMENT_TYPE_LABELS) as EsignDocumentType[]).map((k) => (
                  <option key={k} value={k}>{DOCUMENT_TYPE_LABELS[k]}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={doSaveTemplate}
                disabled={tplBusy || tplName.trim().length < 2}
                className="mt-2 min-h-[40px] w-full rounded-md bg-tcs-accent-ink px-3 text-sm font-semibold text-tcs-accent-on disabled:opacity-50"
              >
                {tplBusy ? 'Saving…' : 'Save template'}
              </button>
            </div>
          )}

          {tplError && (
            <p role="alert" className="mt-2 text-xs text-st-danger-fg">{tplError}</p>
          )}
        </div>
      )}
    </section>
  );
}
